import React, { useState, useEffect } from "react";
import { usePagination } from "../hooks";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputBase,
  IconButton,
  MenuItem,
  TablePagination,
  CircularProgress,
  Divider,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { translateText } from "../translations/translationService";
import { useSelector } from "react-redux";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Pending as PendingIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { CONTROL_REPARACIONES_SCREEN_COLORS, BRAND_COLORS, BORDER_COLORS, TEXT_COLORS } from "../style/colors.js";
import { getFullControlDeTrabajoExterno, insertDatosTrabajoExterno } from "../services/ConnectDataBase";
import { SCREEN_IDS } from "../components/FullComponent.js";
import * as XLSX from "xlsx";
import { getErrorMessage, notify } from "../services/ui/notify";
import AppButton from "../components/Common/ui/AppButton";
import AppTextField from "../components/Common/ui/AppTextField";
import AppSelect from "../components/Common/ui/AppSelect";

export default function ControlDeTrabajoExterno() {
  const lang = useSelector((state) => state.lang.value);
  const name = useSelector((state) => state.user.name);
  const [searchText, setSearchText] = useState("");
  const [trabajoData, setTrabajoData] = useState([]); // array of { work_order_number, pieces: [] }
  const [savedTrabajoData, setSavedTrabajoData] = useState([]); // array of { work_order_number, pieces: [] }
  const [isLoading, setIsLoading] = useState(true);
  const [expandedWo, setExpandedWo] = useState(new Set()); // Set of work_order_number for Pendientes
  const [expandedWoHistorico, setExpandedWoHistorico] = useState(new Set()); // Set for Histórico
  const [savingPieceId, setSavingPieceId] = useState(null); // { wo, mo, ref } while saving
  // Hook de paginación
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(0, 100);
  const [currentView, setCurrentView] = useState("Pendientes");
  const [historicoDate, setHistoricoDate] = useState("");
  const [filterUsuario, setFilterUsuario] = useState("");

  const pdfUrl = "https://new.facetbarcelona.com/JTS/php/PdfOrdenesTrabajo/";

  const uniqueUsuarios = React.useMemo(() => {
    const set = new Set();
    (trabajoData || []).forEach(wo => (wo.pieces || []).forEach(p => {
      const u = p.usuario || p.Usuario;
      if (u) set.add(u);
    }));
    (savedTrabajoData || []).forEach(wo => (wo.pieces || []).forEach(p => {
      const u = p.usuario || p.Usuario;
      if (u) set.add(u);
    }));
    return Array.from(set).sort();
  }, [trabajoData, savedTrabajoData]);

  const roundPesoForDisplay = (v) => {
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : Math.round(n * 100) / 100;
  };

  const getStoredPesoEntregado = (p) =>
    roundPesoForDisplay(p?.PesoEntregado ?? p?.peso_entregado);

  /** Solo el peso entregado guardado (sin autorellenar con peso_repaso). */
  const getEffectivePesoEntregado = (p) => getStoredPesoEntregado(p);

  // Normaliza "WO000187" => 187 para poder ordenar correctamente por número.
  const parseWoNumber = (woNum) => {
    if (!woNum) return 0;
    const digits = String(woNum).replace(/\D/g, "");
    const n = Number(digits);
    return Number.isNaN(n) ? 0 : n;
  };

  const normalizeWoListPesos = (woList) =>
    (woList || []).map(wo => ({
      ...wo,
      pieces: (wo.pieces || []).map(p => {
        const pe = getStoredPesoEntregado(p);
        const pd = roundPesoForDisplay(p.PesoDevolucion ?? p.peso_devolucion);
        const pr = roundPesoForDisplay(p.peso_repaso);
        return {
          ...p,
          ...(pr != null && { peso_repaso: pr }),
          ...(pe != null && { PesoEntregado: pe }),
          ...(pd != null && { PesoDevolucion: pd })
        };
      })
    }));

  const sortWoListDesc = (woList) =>
    [...(woList || [])].sort(
      (a, b) => parseWoNumber(b.work_order_number) - parseWoNumber(a.work_order_number)
    );

  useEffect(() => {
    const fetchWork = async () => {
      try {
        setIsLoading(true);
        const work = await getFullControlDeTrabajoExterno(
          SCREEN_IDS.GENERAL,
          "TrabajoExterno"
        );

        const data = Array.isArray(work) ? work : [];
        const pendientes = sortWoListDesc(
          normalizeWoListPesos(data.filter(wo => wo.pieces?.some(p => !p.FechaDevolucion)))
        );
        const historico = sortWoListDesc(
          normalizeWoListPesos(
            data.filter(wo => wo.pieces?.every(p => p.FechaDevolucion))
          )
        );

        setTrabajoData(pendientes);
        setSavedTrabajoData(historico);
      } catch (error) {
        console.error("Error al cargar trabajos:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWork();
  }, []);

  const handleClearSearch = () => {
    setSearchText("");
  };

  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value);
  };


  const toggleExpandPendiente = (woNum) => {
    setExpandedWo(prev => {
      const next = new Set(prev);
      if (next.has(woNum)) next.delete(woNum);
      else next.add(woNum);
      return next;
    });
  };

  const toggleExpandHistorico = (woNum) => {
    setExpandedWoHistorico(prev => {
      const next = new Set(prev);
      if (next.has(woNum)) next.delete(woNum);
      else next.add(woNum);
      return next;
    });
  };

  const handlePieceChange = (workOrderNumber, mo, referencia, field, value) => {
    setTrabajoData(prev => prev.map(wo => {
      if (wo.work_order_number !== workOrderNumber) return wo;
      return {
        ...wo,
        pieces: (wo.pieces || []).map(p =>
          p.mo === mo && p.referencia === referencia ? { ...p, [field]: value } : p
        )
      };
    }));
  };

  const handleSaveSinglePiece = async (piece) => {
    const id = `${piece.work_order_number}-${piece.mo}-${piece.referencia}`;
    setSavingPieceId(id);
    try {
      const payload = [buildPayloadPiece(piece)];
      await insertDatosTrabajoExterno(SCREEN_IDS.GENERAL, "insertarDatosTrabajoExterno", payload);
      const work = await getFullControlDeTrabajoExterno(SCREEN_IDS.GENERAL, "TrabajoExterno");
      const data = Array.isArray(work) ? work : [];
      const pendientes = sortWoListDesc(
        normalizeWoListPesos(data.filter(wo => wo.pieces?.some(p => !p.FechaDevolucion)))
      );
      const historico = sortWoListDesc(
        normalizeWoListPesos(
          data.filter(wo => wo.pieces?.every(p => p.FechaDevolucion))
        )
      );
      setTrabajoData(pendientes);
      setSavedTrabajoData(historico);
    } catch (error) {
      console.error("Error guardando pieza:", error);
      notify.error(getErrorMessage(error, translateText("Error al guardar", lang)));
    } finally {
      setSavingPieceId(null);
    }
  };

  const handleSaveData = async () => {
    try {
      const originalWork = await getFullControlDeTrabajoExterno(SCREEN_IDS.GENERAL, "TrabajoExterno");
      const data = Array.isArray(originalWork) ? originalWork : [];
      const originalPendientes = data.filter(wo => wo.pieces?.some(p => !p.FechaDevolucion));
      const allPieces = trabajoData.flatMap(wo => (wo.pieces || []).map(p => ({ ...p, work_order_number: wo.work_order_number })));
      const datosParaGuardar = allPieces.filter((item) => {
        const origWo = originalPendientes.find(wo => wo.work_order_number === item.work_order_number);
        const originalItem = origWo?.pieces?.find(p => p.mo === item.mo && p.referencia === item.referencia);
        if (!originalItem) return false;
        const pesoEnt = getEffectivePesoEntregado(item);
        const pesoDev = item.PesoDevolucion ?? item.peso_devolucion;
        const origPesoEnt = getStoredPesoEntregado(originalItem);
        const origPesoDev = originalItem.PesoDevolucion ?? originalItem.peso_devolucion;
        const hayCambios =
          (item.trabajo || "Engastado") !== (originalItem.trabajo || "Engastado") ||
          Number(pesoEnt) !== Number(origPesoEnt) ||
          Number(pesoDev) !== Number(origPesoDev);
        return hayCambios;
      }).map(item => buildPayloadPiece(item));

      if (datosParaGuardar.length === 0) {
        notify.info("No hay cambios para guardar.");
        return;
      }
      await insertDatosTrabajoExterno(SCREEN_IDS.GENERAL, "insertarDatosTrabajoExterno", datosParaGuardar);
      const work = await getFullControlDeTrabajoExterno(SCREEN_IDS.GENERAL, "TrabajoExterno");
      const refreshed = Array.isArray(work) ? work : [];
      const pendientes = sortWoListDesc(
        normalizeWoListPesos(refreshed.filter(wo => wo.pieces?.some(p => !p.FechaDevolucion)))
      );
      const historico = sortWoListDesc(
        normalizeWoListPesos(
          refreshed.filter(wo => wo.pieces?.every(p => p.FechaDevolucion))
        )
      );
      setTrabajoData(pendientes);
      setSavedTrabajoData(historico);
      notify.success("Datos guardados correctamente");
    } catch (error) {
      console.error("Error guardando datos:", error);
      notify.error(getErrorMessage(error, "Error al guardar datos"));
    }
  };

  const filteredTrabajoData = trabajoData.filter((wo) => {
    if (filterUsuario) {
      const hasUser = (wo.pieces || []).some(p => (p.usuario || p.Usuario || "") === filterUsuario);
      if (!hasUser) return false;
    }
    if (!searchText) return true;
    const t = searchText.toLowerCase();
    if ((wo.work_order_number || "").toLowerCase().includes(t)) return true;
    return (wo.pieces || []).some(p =>
      (p.mo || "").toLowerCase().includes(t) || (p.referencia || "").toLowerCase().includes(t)
    );
  });

  const getPieceFecha = (p) => {
    const f = p.fecha_entrega_pieza;
    if (!f) return "";
    return typeof f === "string" ? f.split(" ")[0] : (f.date || "").split(" ")[0];
  };

  const buildPayloadPiece = (p) => {
    const f = p.fecha_entrega_pieza;
    const dateStr = !f ? "" : typeof f === "string" ? f.split(" ")[0] : (f.date || "").split(" ")[0];
    const pesoEnt = getEffectivePesoEntregado(p);
    const pesoDev = p.PesoDevolucion ?? p.peso_devolucion;
    return {
      mo: p.mo,
      fecha_entrega_pieza: { date: dateStr },
      peso_entregado: roundPesoTo2(pesoEnt),
      peso_devuelto: roundPesoTo2(pesoDev),
      trabajo: p.trabajo || "Engastado",
      observaciones_trabajo: p.observaciones_trabajo ?? p.observaciones ?? null,
      usuario: p.usuario || p.Usuario || null
    };
  };

  const filteredHistoricoByDate = (savedTrabajoData || []).filter((wo) => {
    if (historicoDate === "") return true;
    return (wo.pieces || []).some(p => getPieceFecha(p) === historicoDate);
  });

  const filteredHistoricoData = savedTrabajoData.filter((wo) => {
    if (filterUsuario) {
      const hasUser = (wo.pieces || []).some(p => (p.usuario || p.Usuario || "") === filterUsuario);
      if (!hasUser) return false;
    }
    const t = searchText.toLowerCase();
    const matchesSearch = !searchText ||
      (wo.work_order_number || "").toLowerCase().includes(t) ||
      (wo.pieces || []).some(p => {
        const mo = (p.mo || "").toLowerCase();
        const ref = (p.referencia || "").toLowerCase();
        const trab = (p.trabajo || "").toLowerCase();
        const obs = ((p.observaciones_trabajo || p.observaciones) || "").toLowerCase();
        return mo.includes(t) || ref.includes(t) || trab.includes(t) || obs.includes(t);
      });
    const matchesDate = historicoDate === "" || (wo.pieces || []).some(p => getPieceFecha(p) === historicoDate);
    return matchesSearch && matchesDate;
  });

  /** Convierte fecha BD/string a Date local (sin hora) para Excel tipado. */
  const toExcelDate = (raw) => {
    if (!raw) return null;
    let yyyy;
    let mm;
    let dd;
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
      return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
    }
    if (typeof raw === "object" && raw.date) {
      [yyyy, mm, dd] = String(raw.date).split(" ")[0].split("-");
    } else if (typeof raw === "string") {
      const s = raw.split(" ")[0];
      if (s.includes("-")) {
        [yyyy, mm, dd] = s.split("-");
      } else if (s.includes("/")) {
        [dd, mm, yyyy] = s.split("/");
      }
    }
    if (!yyyy || !mm || !dd) return null;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const toExcelNumber = (v) => {
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const buildTrabajoExternoExcelRow = (wo, p, { defaultTrabajo = "" } = {}) => {
    const pesoEnt = toExcelNumber(getEffectivePesoEntregado(p));
    const pesoDev = toExcelNumber(p.PesoDevolucion ?? p.peso_devolucion);
    const pesoPiedras = toExcelNumber(sumPiedrasPeso(p));
    const pesoDevMenosPiedras = toExcelNumber(calcPesoDevMenosPiedras(p));
    const merma = toExcelNumber(calcMerma(p));

    return {
      "Orden trabajo": wo.work_order_number || "",
      MO: p.mo,
      Referencia: p.referencia || "",
      Fecha: toExcelDate(p.fecha_entrega_pieza) || toExcelDate(getPieceFecha(p)) || "",
      Trabajo: p.trabajo || defaultTrabajo,
      Usuario: p.usuario || p.Usuario || "",
      Observaciones: p.observaciones_trabajo || p.observaciones || "",
      "Peso Entregado": pesoEnt,
      "Peso Devolución": pesoDev,
      "Peso Piedras": pesoPiedras,
      "Peso Dev. - Piedras": pesoDevMenosPiedras,
      Merma: merma,
      "Fecha Devolución": toExcelDate(p.FechaDevolucion) || "",
    };
  };

  const applyTrabajoExternoExcelTypes = (worksheet) => {
    if (!worksheet["!ref"]) return;
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    const headerRow = 0;
    const headers = [];
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: headerRow, c: C })];
      headers[C] = cell?.v || "";
    }

    const dateCols = new Set();
    const numberCols = new Set();
    const weightHeaders = new Set([
      "Peso Entregado",
      "Peso Devolución",
      "Peso Piedras",
      "Peso Dev. - Piedras",
      "Merma",
    ]);
    headers.forEach((h, c) => {
      if (h === "Fecha" || h === "Fecha Devolución") dateCols.add(c);
      if (weightHeaders.has(h)) numberCols.add(c);
    });

    for (let R = range.s.r + 1; R <= range.e.r; R++) {
      dateCols.forEach((C) => {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = worksheet[addr];
        if (!cell || cell.v == null || cell.v === "") return;
        const d = cell.v instanceof Date ? cell.v : toExcelDate(cell.v);
        if (!d) return;
        worksheet[addr] = { t: "d", v: d, z: "dd/mm/yyyy" };
      });
      numberCols.forEach((C) => {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = worksheet[addr];
        if (!cell || cell.v == null || cell.v === "") return;
        const n = typeof cell.v === "number" ? cell.v : toExcelNumber(cell.v);
        if (n == null) return;
        worksheet[addr] = { t: "n", v: n, z: "0.0000" };
      });
    }
  };

  const downloadTrabajoExternoExcel = (rows, sheetName, fileName) => {
    const worksheet = XLSX.utils.json_to_sheet(rows, { cellDates: true });
    const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
    worksheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };
    worksheet["!cols"] = [
      { wch: 14 }, // Orden trabajo
      { wch: 12 }, // MO
      { wch: 15 }, // Referencia
      { wch: 12 }, // Fecha
      { wch: 15 }, // Trabajo
      { wch: 20 }, // Usuario
      { wch: 30 }, // Observaciones
      { wch: 14 }, // Peso Entregado
      { wch: 14 }, // Peso Devolución
      { wch: 13 }, // Peso Piedras
      { wch: 18 }, // Peso Dev. - Piedras
      { wch: 12 }, // Merma
      { wch: 15 }, // Fecha Devolución
    ];
    applyTrabajoExternoExcelTypes(worksheet);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, fileName, { cellDates: true, bookType: "xlsx" });
  };

  const handleExportToExcel = () => {
    const dataToExport = filteredHistoricoData.flatMap((wo) =>
      (wo.pieces || []).map((p) => buildTrabajoExternoExcelRow(wo, p))
    );
    downloadTrabajoExternoExcel(
      dataToExport,
      "Histórico Trabajo Externo",
      "Historico_Trabajo_Externo.xlsx"
    );
  };

  const handleExportPendientesToExcel = () => {
    const dataToExport = filteredTrabajoData.flatMap((wo) =>
      (wo.pieces || []).map((p) =>
        buildTrabajoExternoExcelRow(wo, p, { defaultTrabajo: "Engastado" })
      )
    );
    downloadTrabajoExternoExcel(
      dataToExport,
      "Pendientes Trabajo Externo",
      "Pendientes_Trabajo_Externo.xlsx"
    );
  };

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return "";
    
    // Si es un objeto con date, usar la propiedad date
    if (typeof fechaStr === 'object' && fechaStr.date) {
      const [yyyy, mm, dd] = fechaStr.date.split(" ")[0].split("-");
      return `${dd}/${mm}/${yyyy}`;
    }
    
    // Si es un string, usar el formato original
    if (typeof fechaStr === 'string') {
      const [yyyy, mm, dd] = fechaStr.split(" ")[0].split("-");
      return `${dd}/${mm}/${yyyy}`;
    }
    
    return "";
  };

  const formatUsuarioDisplay = (str) => {
    if (!str || typeof str !== "string") return "—";
    const cleaned = str.replace(/^EXTERNO_/i, "").trim();
    return cleaned || str;
  };

  const formatPeso = (v) => {
    if (v == null || v === "") return "";
    const n = Number(v);
    if (Number.isNaN(n)) return "";
    return n.toFixed(2);
  };

  const formatPeso4 = (v) => {
    if (v == null || v === "") return "";
    const n = Number(v);
    if (Number.isNaN(n)) return "";
    return n.toFixed(4);
  };

  const sumPiedrasPeso = (piece) => {
    const arr = Array.isArray(piece?.piedras) ? piece.piedras : [];
    return arr.reduce((acc, s) => {
      const n = Number(s?.peso);
      return acc + (Number.isNaN(n) ? 0 : n);
    }, 0);
  };

  const calcPesoDevMenosPiedras = (piece) => {
    const pesoDev = piece?.PesoDevolucion ?? piece?.peso_devolucion;
    if (pesoDev == null || pesoDev === "") return null;
    const dev = Number(pesoDev);
    if (Number.isNaN(dev)) return null;
    return dev - sumPiedrasPeso(piece);
  };

  const calcMerma = (piece) => {
    const pesoEnt = getEffectivePesoEntregado(piece);
    if (pesoEnt == null || pesoEnt === "") return null;
    const ent = Number(pesoEnt);
    if (Number.isNaN(ent)) return null;
    const net = calcPesoDevMenosPiedras(piece);
    if (net == null) return null;
    const netN = Number(net);
    if (Number.isNaN(netN)) return null;
    return ent - netN;
  };

  const roundPesoTo2 = (v) => {
    if (v == null || v === "") return null;
    const n = Number(v);
    if (Number.isNaN(n)) return null;
    return Math.round(n * 100) / 100;
  };

  // Skeleton loader para mejor UX
  const SkeletonRowTrabajoExterno = () => (
    <TableRow>
      {[...Array(10)].map((_, i) => (
        <TableCell key={i} sx={{ py: 0.75, px: 1.5 }}>
          <Skeleton variant="text" width="100%" height={20} />
        </TableCell>
      ))}
    </TableRow>
  );

  const pendientesHeaders = [
    "MO",
    translateText("Referencia", lang),
    translateText("Fecha", lang),
    translateText("Trabajo", lang),
    translateText("Observaciones", lang),
    translateText("Fecha Devolución", lang),
    translateText("Usuario", lang),
    translateText("Peso Entregado", lang),
    translateText("Peso Devolución", lang),
    "",
  ];

  const renderPendientesTable = () => {
    return (
      <>
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_LIGHT}`,
          }}>
          {isLoading ? (
            <Box sx={{ p: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {pendientesHeaders.map((header, i) => (
                        <TableCell key={i} sx={{ py: 1, px: 1.5 }}>
                          <Skeleton variant="text" width="100%" height={24} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(5)].map((_, i) => (
                      <SkeletonRowTrabajoExterno key={i} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: "calc(78vh - 60px)" }}>
              <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 920 }}>
                <TableHead>
                  <TableRow>
                    {pendientesHeaders.map((header, idx) => (
                      <TableCell
                        key={header}
                        align="center"
                        sx={{
                          fontWeight: 600,
                          backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                          borderBottom: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                          fontSize: "0.7rem",
                          color: "black",
                          py: 0.6,
                          px: 1,
                          whiteSpace: "nowrap",
                          width: idx === 6 ? "20%" : undefined,
                        }}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTrabajoData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((wo) => (
                      <React.Fragment key={wo.work_order_number}>
                        <TableRow
                          onClick={() => toggleExpandPendiente(wo.work_order_number)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB",
                            },
                            borderBottom: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
                          }}>
                          <TableCell colSpan={10} align="center" sx={{ fontSize: "0.75rem", py: 0.5, px: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, flexWrap: "nowrap" }}>
                              {expandedWo.has(wo.work_order_number) ? (
                                <ExpandLessIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                              ) : (
                                <ExpandMoreIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                              )}
                              <Typography component="span" sx={{ fontWeight: 600, flexShrink: 0 }}>
                                {wo.work_order_number}
                              </Typography>
                              {wo.work_order_number && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`${pdfUrl}${wo.work_order_number}.pdf`, "_blank");
                                  }}
                                  sx={{
                                    padding: 0.25,
                                    color: BRAND_COLORS.PRIMARY,
                                    flexShrink: 0,
                                    "&:hover": { backgroundColor: "rgba(70, 93, 100, 0.1)" },
                                  }}
                                  title={`${translateText("Ver orden de trabajo:", lang)} ${wo.work_order_number}`}
                                >
                                  <AssignmentIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                              <Typography component="span" sx={{ color: "text.secondary", fontSize: "0.75rem", flexShrink: 0 }}>
                                {(wo.pieces || []).length} {translateText("pieza(s)", lang)}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                        {expandedWo.has(wo.work_order_number) &&
                          (wo.pieces || []).map((p) => {
                            const pieceId = `${wo.work_order_number}-${p.mo}-${p.referencia}`;
                            const isSaving = savingPieceId === pieceId;
                            const usuarioText = formatUsuarioDisplay(p.usuario || p.Usuario);
                            return (
                              <TableRow
                                key={pieceId}
                                sx={{
                                  backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_ROW_ALTERNATE || "rgba(0,0,0,0.02)",
                                  borderBottom: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
                                }}
                              >
                                <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.35, px: 1 }}>{p.mo}</TableCell>
                                <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.35, px: 1 }}>{p.referencia}</TableCell>
                                <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.35, px: 1 }}>{formatFecha(typeof p.fecha_entrega_pieza === "string" ? p.fecha_entrega_pieza : p.fecha_entrega_pieza?.date)}</TableCell>
                                <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.35, px: 1 }}>{p.trabajo || "Engastado"}</TableCell>
                                <TableCell align="center" sx={{ fontSize: "0.7rem", py: 0.35, px: 1, maxWidth: 120 }}>
                                  <Tooltip title={p.observaciones_trabajo || p.observaciones || ""} arrow>
                                    <Typography sx={{ fontSize: "0.7rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {p.observaciones_trabajo || p.observaciones || "—"}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.35, px: 1 }}>{formatFecha(p.FechaDevolucion)}</TableCell>
                                <TableCell align="center" sx={{ fontSize: "12.4px", py: 0.35, px: 1, minWidth: 0, width: "20%" }}>
                                  <Tooltip title={usuarioText} arrow>
                                    <Typography sx={{ fontSize: "12.4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "center" }}>
                                      {usuarioText}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.35, px: 1 }}>
                                  <AppTextField
                                    type="number"
                                    inputProps={{ step: 0.01, min: 0 }}
                                    value={getEffectivePesoEntregado(p) ?? ""}
                                    onChange={(e) => handlePieceChange(wo.work_order_number, p.mo, p.referencia, "PesoEntregado", e.target.value)}
                                    onBlur={(e) => {
                                      const v = e.target.value;
                                      if (v !== "" && !Number.isNaN(Number(v))) {
                                        handlePieceChange(wo.work_order_number, p.mo, p.referencia, "PesoEntregado", String(Number(v).toFixed(2)));
                                      }
                                    }}
                                    sx={{
                                      width: 80,
                                      "& .MuiOutlinedInput-root": { minHeight: 26, height: 26, "& input": { textAlign: "center", fontSize: "0.75rem", py: 0 } },
                                      "& input[type=number]": { MozAppearance: "textfield" },
                                      "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.35, px: 1 }}>
                                  <AppTextField
                                    type="number"
                                    inputProps={{ step: 0.01, min: 0 }}
                                    value={p.PesoDevolucion ?? p.peso_devolucion ?? ""}
                                    onChange={(e) => handlePieceChange(wo.work_order_number, p.mo, p.referencia, "PesoDevolucion", e.target.value)}
                                    onBlur={(e) => {
                                      const v = e.target.value;
                                      if (v !== "" && !Number.isNaN(Number(v))) {
                                        handlePieceChange(wo.work_order_number, p.mo, p.referencia, "PesoDevolucion", String(Number(v).toFixed(2)));
                                      }
                                    }}
                                    sx={{
                                      width: 80,
                                      "& .MuiOutlinedInput-root": { minHeight: 26, height: 26, "& input": { textAlign: "center", fontSize: "0.75rem", py: 0 } },
                                      "& input[type=number]": { MozAppearance: "textfield" },
                                      "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.35, px: 1 }}>
                                  <AppButton
                                    variantType="secondary"
                                    disabled={isSaving}
                                    onClick={() => handleSaveSinglePiece({ ...p, work_order_number: wo.work_order_number })}
                                    sx={{ fontSize: "0.65rem", minWidth: 84, py: 0.25, minHeight: 28 }}
                                  >
                                    {isSaving ? <CircularProgress size={16} /> : translateText("Guardar", lang)}
                                  </AppButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </React.Fragment>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!isLoading && (
            <>
              <Divider />

              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                count={filteredTrabajoData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_ROW_ALTERNATE,
                  "& .MuiTablePagination-toolbar": {
                    fontSize: "0.75rem",
                    color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_SECONDARY,
                    minHeight: "52px",
                  },
                  "& .MuiTablePagination-select": {
                    fontSize: "0.75rem",
                  },
                  "& .MuiTablePagination-displayedRows": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </>
          )}
        </Paper>

        {!isLoading && (
          <Box
            sx={{
              marginTop: 1.5,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}>
            <AppButton
              variantType="primary"
              startIcon={<SaveIcon />}
              sx={{
                backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY,
                color: "white",
                border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
                borderRadius: 2,
                fontSize: "0.8rem",
                fontWeight: 600,
                padding: "6px 16px",
                textTransform: "none",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 8px rgba(70, 93, 100, 0.2)",
                "&:hover": {
                  backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY,
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
                },
              }}
              onClick={handleSaveData}>
              {translateText("Guardar Datos", lang)}
            </AppButton>
          </Box>
        )}
      </>
    );
  };

  const renderHistoricoTable = () => {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 2,
            gap: 2,
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}>
            <AppTextField
              label={translateText("Seleccionar Fecha", lang)}
              type='date'
              value={historicoDate}
              onChange={(e) => setHistoricoDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: "140px",
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
                "& .MuiOutlinedInput-input": {
                  fontSize: "0.75rem",
                  py: 0.75,
                },
              }}
            />

            <AppButton
              variantType="secondary"
              sx={{
                backgroundColor: "transparent",
                color: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PURPLE,
                border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_PURPLE}`,
                borderRadius: 2,
                fontSize: "0.75rem",
                fontWeight: 500,
                padding: "4px 12px",
                textTransform: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PURPLE,
                  color: "white",
                  transform: "translateY(-1px)",
                  boxShadow: "0 2px 8px rgba(156, 39, 176, 0.2)",
                  border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_PURPLE}`,
                },
              }}
              onClick={() => setHistoricoDate("")}>
              {translateText("Quitar filtro", lang)}
            </AppButton>
          </Box>

          <AppButton
            variantType="secondary"
            sx={{
              backgroundColor: "transparent",
              color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
              border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
              borderRadius: 2,
              fontSize: "0.75rem",
              fontWeight: 500,
              padding: "4px 12px",
              textTransform: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY,
                color: "white",
                transform: "translateY(-1px)",
                boxShadow: "0 2px 8px rgba(70, 93, 100, 0.2)",
                border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
              },
            }}
            onClick={handleExportToExcel}>
            {translateText("Exportar excel", lang)}
          </AppButton>
        </Box>

        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_LIGHT}`,
          }}>
          {isLoading ? (
            <Box sx={{ p: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["MO", translateText("Referencia", lang), translateText("Fecha", lang), translateText("Trabajo", lang), translateText("Observaciones", lang), translateText("Fecha Devolución", lang), translateText("Usuario", lang), translateText("Peso Entregado", lang), translateText("Peso Devolución", lang), translateText("Peso Dev. - Piedras", lang), translateText("Merma", lang)].map((header, i) => (
                      <TableCell key={i} sx={{ py: 1, px: 1.5 }}>
                        <Skeleton variant="text" width="100%" height={24} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, i) => (
                    <SkeletonRowTrabajoExterno key={i} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: "calc(78vh - 60px)" }}>
              <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 920 }}>
                <TableHead>
                  <TableRow>
                    {["MO", translateText("Referencia", lang), translateText("Fecha", lang), translateText("Trabajo", lang), translateText("Observaciones", lang), translateText("Fecha Devolución", lang), translateText("Usuario", lang), translateText("Peso Entregado", lang), translateText("Peso Devolución", lang), translateText("Peso Dev. - Piedras", lang), translateText("Merma", lang)].map((header, idx) => (
                      <TableCell
                        key={header}
                        align="center"
                        sx={{
                          fontWeight: 600,
                          backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                          borderBottom: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                          fontSize: "0.7rem",
                          color: "black",
                          py: 0.6,
                          px: 1,
                          whiteSpace: "nowrap",
                          width: idx === 6 ? "20%" : undefined,
                        }}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHistoricoData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                        <Typography variant="h6" sx={{ color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280" }}>
                          {translateText("No hay resultados para la búsqueda o la fecha seleccionada.", lang)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredHistoricoData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((wo) => (
                        <React.Fragment key={wo.work_order_number}>
                          <TableRow
                            onClick={() => toggleExpandHistorico(wo.work_order_number)}
                            sx={{
                              cursor: "pointer",
                              "&:hover": { backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_ROW || "#F9FAFB" },
                              borderBottom: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_SECONDARY}`,
                            }}
                          >
                            <TableCell colSpan={11} align="center" sx={{ fontSize: "0.75rem", py: 0.5, px: 1 }}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, flexWrap: "nowrap" }}>
                                {expandedWoHistorico.has(wo.work_order_number) ? (
                                  <ExpandLessIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                                ) : (
                                  <ExpandMoreIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                                )}
                                <Typography component="span" sx={{ fontWeight: 600, flexShrink: 0 }}>
                                  {wo.work_order_number}
                                </Typography>
                                {wo.work_order_number && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(`${pdfUrl}${wo.work_order_number}.pdf`, "_blank");
                                    }}
                                    sx={{
                                      padding: 0.25,
                                      color: BRAND_COLORS.PRIMARY,
                                      flexShrink: 0,
                                      "&:hover": { backgroundColor: "rgba(70, 93, 100, 0.1)" },
                                    }}
                                    title={`${translateText("Ver orden de trabajo:", lang)} ${wo.work_order_number}`}
                                  >
                                    <AssignmentIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                )}
                                <Typography component="span" sx={{ color: "text.secondary", fontSize: "0.75rem", flexShrink: 0 }}>
                                  {(wo.pieces || []).length} {translateText("pieza(s)", lang)}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                          {expandedWoHistorico.has(wo.work_order_number) &&
                            (wo.pieces || []).map((p) => {
                              const usuarioText = formatUsuarioDisplay(p.usuario || p.Usuario);
                              const pesoDevMenosPiedras = calcPesoDevMenosPiedras(p);
                              const merma = calcMerma(p);
                              return (
                                <TableRow
                                  key={`${wo.work_order_number}-${p.mo}-${p.referencia}`}
                                  sx={{
                                    backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_ROW_ALTERNATE || "rgba(0,0,0,0.02)",
                                    borderBottom: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_SECONDARY}`,
                                  }}
                                >
                                  <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.35, px: 1, color: TEXT_COLORS.PRIMARY }}>{p.mo}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.35, px: 1, color: TEXT_COLORS.PRIMARY }}>{p.referencia}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.35, px: 1, color: TEXT_COLORS.PRIMARY }}>{formatFecha(typeof p.fecha_entrega_pieza === "string" ? p.fecha_entrega_pieza : p.fecha_entrega_pieza?.date)}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.35, px: 1, color: TEXT_COLORS.PRIMARY }}>{p.trabajo || ""}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: "0.7rem", py: 0.35, px: 1, maxWidth: 120 }}>
                                    <Tooltip title={p.observaciones_trabajo || p.observaciones || ""} arrow>
                                      <Typography sx={{ fontSize: "0.7rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {p.observaciones_trabajo || p.observaciones || "—"}
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.35, px: 1, color: TEXT_COLORS.PRIMARY }}>{formatFecha(p.FechaDevolucion)}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: "12.4px", py: 0.35, px: 1, minWidth: 0, width: "20%", color: TEXT_COLORS.PRIMARY }}>
                                    <Tooltip title={usuarioText} arrow>
                                      <Typography sx={{ fontSize: "12.4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "center" }}>
                                        {usuarioText}
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.35, px: 1, color: TEXT_COLORS.PRIMARY }}>{formatPeso4(p.PesoEntregado ?? p.peso_entregado) || "—"}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.35, px: 1, color: TEXT_COLORS.PRIMARY }}>{formatPeso4(p.PesoDevolucion ?? p.peso_devolucion) || "—"}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.35, px: 1, color: TEXT_COLORS.PRIMARY }}>
                                    {pesoDevMenosPiedras == null ? "—" : formatPeso4(pesoDevMenosPiedras)}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.35, px: 1, color: TEXT_COLORS.PRIMARY }}>
                                    {merma == null ? "—" : formatPeso4(merma)}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          {expandedWoHistorico.has(wo.work_order_number) && (
                            (() => {
                              const pieces = wo.pieces || [];
                              const totals = pieces.reduce(
                                (acc, p) => {
                                  const pe = Number(p.PesoEntregado ?? p.peso_entregado);
                                  const pd = Number(p.PesoDevolucion ?? p.peso_devolucion);
                                  const net = calcPesoDevMenosPiedras(p);
                                  const m = calcMerma(p);
                                  acc.pesoEnt += Number.isNaN(pe) ? 0 : pe;
                                  acc.pesoDev += Number.isNaN(pd) ? 0 : pd;
                                  acc.pesoDevMenosPiedras += net == null || Number.isNaN(Number(net)) ? 0 : Number(net);
                                  acc.merma += m == null || Number.isNaN(Number(m)) ? 0 : Number(m);
                                  return acc;
                                },
                                { pesoEnt: 0, pesoDev: 0, pesoDevMenosPiedras: 0, merma: 0 }
                              );

                              return (
                                <TableRow
                                  sx={{
                                    backgroundColor: "rgba(70, 93, 100, 0.08)",
                                    borderTop: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_SECONDARY}`,
                                    borderBottom: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_SECONDARY}`,
                                  }}
                                >
                                  <TableCell
                                    colSpan={7}
                                    align="right"
                                    sx={{
                                      fontSize: "0.75rem",
                                      py: 0.45,
                                      px: 1,
                                      fontWeight: 700,
                                      color: TEXT_COLORS.PRIMARY,
                                      letterSpacing: 0.2,
                                    }}
                                  >
                                    {translateText("TOTAL", lang)}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.45, px: 1, fontWeight: 700, color: TEXT_COLORS.PRIMARY }}>
                                    {formatPeso4(totals.pesoEnt)}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.45, px: 1, fontWeight: 700, color: TEXT_COLORS.PRIMARY }}>
                                    {formatPeso4(totals.pesoDev)}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.45, px: 1, fontWeight: 700, color: TEXT_COLORS.PRIMARY }}>
                                    {formatPeso4(totals.pesoDevMenosPiedras)}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: "0.75rem", py: 0.45, px: 1, fontWeight: 700, color: TEXT_COLORS.PRIMARY }}>
                                    {formatPeso4(totals.merma)}
                                  </TableCell>
                                </TableRow>
                              );
                            })()
                          )}
                        </React.Fragment>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!isLoading && (
            <>
              <Divider />

              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                count={filteredHistoricoData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_ROW_ALTERNATE,
                  "& .MuiTablePagination-toolbar": {
                    fontSize: "0.75rem",
                    color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_SECONDARY,
                    minHeight: "52px",
                  },
                  "& .MuiTablePagination-select": {
                    fontSize: "0.75rem",
                  },
                  "& .MuiTablePagination-displayedRows": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </>
          )}
        </Paper>
      </>
    );
  };

  return (
    <Box sx={{ padding: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "center",
          gap: 1.5,
          padding: 1.5,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          marginBottom: 1.5,
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
        }}>
        <Box
          sx={{
            flex: "1 1 520px",
            minWidth: "320px",
            position: "relative",
          }}>
          <InputBase
            value={searchText}
            onChange={handleSearchTextChange}
            placeholder={translateText("Buscar por MO, Referencia u Orden de trabajo", lang)}
            sx={{
              width: "100%",
              backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_CARD || "#F9FAFB",
              color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
              fontSize: "0.85rem",
              fontWeight: 500,
              padding: searchText 
                ? "8px 40px 8px 40px" 
                : "8px 12px 8px 40px",
              borderRadius: 2,
              border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_CARD || "#e5e7eb"}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_INPUT || "#F3F4F6",
                borderColor: CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND || "#174045",
                boxShadow: "0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "&.Mui-focused": {
                backgroundColor: "white",
                borderColor: CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND || "#174045",
                boxShadow:
                  "0 0 0 2px rgba(70, 93, 100, 0.1), 0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "& input": {
                padding: 0,
              },
              "& input::placeholder": {
                color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_MUTED || "#9CA3AF",
                opacity: 1,
              },
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_MUTED || "#9CA3AF",
              pointerEvents: "none",
            }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>

          {searchText && (
            <IconButton
              onClick={handleClearSearch}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                padding: 0.5,
                color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_MUTED || "#9CA3AF",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(239, 68, 68, 0.1)",
                  color: "#ef4444",
                  transform: "translateY(-50%) scale(1.1)",
                },
              }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>

        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: "1px",
            height: 32,
            backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_CARD || "#e5e7eb",
          }}
        />

        <AppSelect
          displayEmpty
          value={filterUsuario}
          onChange={(e) => setFilterUsuario(e.target.value || "")}
          renderValue={(v) => v ? formatUsuarioDisplay(v) : translateText("Todos los usuarios", lang)}
          sx={{
            width: 180,
            minWidth: 120,
            maxWidth: 200,
            fontSize: "0.8rem",
            flex: "0 0 auto",
            "& .MuiSelect-select": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        >
          <MenuItem value="">{translateText("Todos los usuarios", lang)}</MenuItem>
          {uniqueUsuarios.map((u) => (
            <MenuItem key={u} value={u} sx={{ fontSize: "0.8rem" }}>{formatUsuarioDisplay(u)}</MenuItem>
          ))}
        </AppSelect>

        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: "1px",
            height: 32,
            backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_CARD || "#e5e7eb",
          }}
        />

        <AppButton
          variantType="secondary"
          startIcon={<PendingIcon />}
          onClick={() => setCurrentView("Pendientes")}
          sx={{
            flex: "0 0 auto",
            backgroundColor:
              currentView === "Pendientes" ? CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY : "transparent",
            color: currentView === "Pendientes" ? "white" : CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
            border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "6px 16px",
            textTransform: "none",
            transition: "all 0.2s ease",
            boxShadow:
              currentView === "Pendientes"
                ? "0 2px 8px rgba(70, 93, 100, 0.2)"
                : "none",
            "&:hover": {
              backgroundColor:
                currentView === "Pendientes"
                  ? CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY
                  : "rgba(70, 93, 100, 0.1)",
              color: currentView === "Pendientes" ? "white" : CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
            },
          }}>
          {translateText("Pendientes", lang)}
        </AppButton>

        <AppButton
          variantType="secondary"
          startIcon={<HistoryIcon />}
          onClick={() => setCurrentView("Historico")}
          sx={{
            flex: "0 0 auto",
            backgroundColor:
              currentView === "Historico" ? CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY : "transparent",
            color: currentView === "Historico" ? "white" : CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
            border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "6px 16px",
            textTransform: "none",
            transition: "all 0.2s ease",
            boxShadow:
              currentView === "Historico"
                ? "0 2px 8px rgba(70, 93, 100, 0.2)"
                : "none",
            "&:hover": {
              backgroundColor:
                currentView === "Historico"
                  ? CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY
                  : "rgba(70, 93, 100, 0.1)",
              color: currentView === "Historico" ? "white" : CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
            },
          }}>
          {translateText("Historico", lang)}
        </AppButton>

        {currentView === "Pendientes" && (
          <>
            <Box
              sx={{
                display: { xs: "none", lg: "block" },
                width: "1px",
                height: 32,
                backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_CARD || "#e5e7eb",
              }}
            />
            <AppButton
              variantType="secondary"
              sx={{
                flex: "0 0 auto",
                backgroundColor: "transparent",
                color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_BRAND,
                border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
                borderRadius: 2,
                fontSize: "0.75rem",
                fontWeight: 500,
                padding: "4px 12px",
                textTransform: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY,
                  color: "white",
                  transform: "translateY(-1px)",
                  boxShadow: "0 2px 8px rgba(70, 93, 100, 0.2)",
                  border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_BRAND}`,
                },
              }}
              onClick={handleExportPendientesToExcel}>
              {translateText("Exportar excel", lang)}
            </AppButton>
          </>
        )}
      </Box>

      {currentView === "Pendientes" ? (
        !isLoading && filteredTrabajoData.length === 0 ? (
          <Paper
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
              textAlign: "center",
              py: 8,
            }}>
            <Typography variant='h6' sx={{ color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280" }}>
              {translateText("No hay resultados para la búsqueda.", lang)}
            </Typography>
          </Paper>
        ) : (
          renderPendientesTable()
        )
      ) : (
        !isLoading && filteredHistoricoData.length === 0 ? (
          <Paper
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
              textAlign: "center",
              py: 8,
            }}>
            <Typography variant='h6' sx={{ color: CONTROL_REPARACIONES_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280" }}>
              {translateText("No hay resultados para la búsqueda o la fecha seleccionada.", lang)}
            </Typography>
          </Paper>
        ) : (
          renderHistoricoTable()
        )
      )}
    </Box>
  );
}